<template>

  <HeaderSectionExtranjeros />

  <section id="formulario" class="bg-gray-100 py-12" style="font-family: PlexRegular;">

    <Form @submit="onSubmit" :validation-schema="schema" class="space-y-4 text-gray-700"
      @invalid-submit="onInvalidSubmit">

      <div class="container mx-auto px-4 max-w-5xl pb-16">

        <!-- Language Switcher -->
        <div class="flex justify-center space-x-4 pb-5">
          <a @click.prevent="changeLanguage('es')" href="#"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded cursor-pointer transition-colors duration-200">
            <img src="@/assets/ar.svg" class="h-8" />
            <span class="font-semibold ml-2 text-gray-800">Español</span>
          </a>
          <a @click.prevent="changeLanguage('en')" href="#"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded cursor-pointer transition-colors duration-200">
            <img src="@/assets/us.svg" class="h-8" />
            <span class="font-semibold ml-2 text-gray-800">English</span>
          </a>
        </div>

        <h1 class="font-bold text-gray-800 mb-4 text-2xl text-center pb-5"
          style="color: rgb(41 57 152); font-family: PlexSemiBold;">
          {{ $t('title') }}<br>
          {{ $t('subtitle') }}
        </h1>

        <!-- Nombre y Apellido -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('nombre') }}</label>
            <Field name="nombre" v-slot="{ field, errors }" v-model="formData.nombre">
              <InputText v-bind="field" type="text" :placeholder="$t('nombre.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="nombre" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('apellido') }}</label>
            <Field name="apellido" v-slot="{ field, errors }" v-model="formData.apellido">
              <InputText v-bind="field" type="text" :placeholder="$t('apellido.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="apellido" />
              </span>
            </Field>
          </div>
        </div>

        <!-- DNI y Email -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('documento') }}</label>
            <Field name="documento" v-slot="{ field, errors }" v-model="formData.documento">
              <InputText v-bind="field" type="text" :placeholder="$t('documento.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="documento" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('mail') }}</label>
            <Field name="mail" v-slot="{ field, errors }" v-model="formData.mail">
              <InputText v-bind="field" type="text" :placeholder="$t('mail.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="mail" />
              </span>
            </Field>
          </div>
        </div>

        <!-- Teléfono -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-3/12">
            <label class="block mb-1">{{ $t('pais.telefono') }}</label>
            <Select v-model="selectedPhoneCountry" filter :options="paisesAll" optionLabel="value"
              :placeholder="$t('pais.residencia.placeholder')" class="input-base" />
          </div>
          <div class="w-full px-2 md:w-2/12">
            <label class="block mb-1">{{ $t('codigo.pais') }}</label>
            <InputText type="text" :model-value="selectedPhoneCountry ? selectedPhoneCountry.code : ''"
              class="input-base" readonly />
          </div>
          <div class="w-full px-2 md:w-3/12">
            <label class="block mb-1">{{ $t('codigo.area') }}</label>
            <Field name="codigoArea" v-slot="{ field, errors }" v-model="formData.codigoArea">
              <InputText v-bind="field" type="text" :placeholder="$t('codigo.area.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="codigoArea" />
              </span>
            </Field>
          </div>
          <div class="w-full px-2 md:w-4/12">
            <label class="block mb-1">{{ $t('telefono.celular') }}</label>
            <Field name="telefonoCelular" v-slot="{ field, errors }" v-model="formData.telefonoCelular">
              <InputText v-bind="field" type="text" :placeholder="$t('telefono.celular.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="telefonoCelular" />
              </span>
            </Field>
          </div>
        </div>

        <!-- País y Empresa -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <!-- País de residencia -->
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('pais.residencia') }}</label>
            <Field name="pais" v-slot="{ handleChange, handleBlur, value, errors }" v-model="selectedCountry">
              <Select @blur="handleBlur" @update:model-value="handleChange" :model-value="value" filter
                :options="paises" optionLabel="value" :placeholder="$t('pais.residencia.placeholder')"
                class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value, 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="pais" />
              </span>
            </Field>
          </div>
          <!-- Empresa -->
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('empresa') }}</label>
            <Field name="empresa" v-slot="{ field, errors }" v-model="formData.empresa">
              <InputText v-bind="field" type="text" :placeholder="$t('empresa.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="empresa" />
              </span>
            </Field>
          </div>
        </div>

        <!-- Industria -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0 mt-3">
          <div class="w-full px-2 md:w-6/12">
            <label class="block mb-1">{{ $t('industria') }}</label>
            <Field name="industria" v-slot="{ field, errors }" v-model="formData.industria">
              <InputText v-bind="field" type="text" :placeholder="$t('industria.placeholder')" class="input-base"
                :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0 }" />
              <span class="text-sm text-red-500 p-error">
                <ErrorMessage name="industria" />
              </span>
            </Field>
          </div>
        </div>



        <!-- Aceptar -->
        <div class="flex flex-wrap py-2 mt-6">
          <button type="submit"
            class="px-6 py-3 bg-transparent border-black hover:bg-black text-black font-semibold hover:text-white border rounded-lg cursor-pointer transition-colors duration-350"
            :disabled="isSubmitting">
            <i v-show="isSubmitting" class="pi pi-spin pi-spinner mr-2"></i>
            <i v-show="!isSubmitting" class="pi pi-check mr-2"></i>{{ $t('aceptar') }}
          </button>
        </div>

      </div>
    </Form>

    <BlockUI :blocked="isSubmitting" fullScreen />

  </section>

</template>

<script lang="ts">
import * as yup from 'yup';

import { defineComponent } from 'vue';
import { Field, Form, ErrorMessage } from 'vee-validate';
import Swal from 'sweetalert2'

import HeaderSectionExtranjeros from './HeaderSectionExtranjeros.vue';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import BlockUI from 'primevue/blockui';

import jsonCountries from '@/data/countries.json';
import idiomaService from '@/services/idioma.services';

import suiteService, { Acreditado, TipoAcreditado, EstadoAcreditado, OrigenAcreditado } from 'qreventos-suite/services';
import suiteConfig from '@/services/suite.config';
import { VERIFICADO, SAVE, ORIGEN_ACREDITADO } from "qreventos-suite/constantes";

interface Pais {
  code: string;
  value: string;
}

export default defineComponent({
  components: {
    HeaderSectionExtranjeros,
    InputText, Select, BlockUI,
    Field, ErrorMessage, Form
  },
  created() {
    this.updateTitle();
  },
  data() {
    return {
      isSubmitting: false,
      paises: (jsonCountries as Pais[]).filter(p => p.value !== 'Argentina'),
      selectedCountry: null as any,
      selectedPhoneCountry: { "code": "+54", "value": "Argentina" } as Pais,
      paisesAll: jsonCountries as Pais[],
      formData: {
        nombre: '',
        apellido: '',
        mail: '',
        documento: '',
        codigoArea: '',
        telefonoCelular: '',
        empresa: '',
        industria: ''
      }
    };
  },
  computed: {
    schema() {
      return yup.object({
        nombre: yup.string().required(this.$t('errors.nombre.required')).min(3, this.$t('errors.nombre.min')),
        apellido: yup.string().required(this.$t('errors.apellido.required')).min(2, this.$t('errors.apellido.min')),
        mail: yup.string().required(this.$t('errors.mail.required')).email(this.$t('errors.mail.invalid')),
        documento: yup.string().required(this.$t('errors.documento.required')).matches(/^[0-9]+$/, this.$t('errors.documento.invalid')).min(6, this.$t('errors.documento.min')),
        pais: yup.object().required(this.$t('errors.pais.required')),
        codigoArea: yup.string().required(this.$t('errors.codigo.area.required')).matches(/^\d+$/, this.$t('errors.codigo.area.invalid')).min(2, this.$t('errors.codigo.area.min')),
        telefonoCelular: yup.string().required(this.$t('errors.telefono.celular.required')).matches(/^\d+$/, this.$t('errors.telefono.celular.invalid')).min(5, this.$t('errors.telefono.celular.min')),
        empresa: yup.string().required(this.$t('errors.empresa.required')),
        industria: yup.string().required(this.$t('errors.industria.required'))
      });
    }
  },
  methods: {
    changeLanguage(lang: string) {
      this.$i18n.locale = lang;
      idiomaService.updateIdioma(lang);
      this.updateTitle();
    },
    updateTitle() {
      document.title = this.$t('title') + " - Expo Rural 2026";
    },
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({
        title: this.$t('alert.atencion'),
        html: this.$t('alert.review.form', { count: errores }),
        icon: "error"
      });
    },
    async onSubmit() {
      this.isSubmitting = true;

      try {
        let acreditadoExistente: Acreditado = await suiteService.findAcreditadoByFilters(suiteConfig.evento, "dni=" + this.formData.documento) as Acreditado;
        if (acreditadoExistente) {
          this.isSubmitting = false;

          Swal.fire({
            title: this.$t('alert.atencion'),
            html: this.$t('alert.registrado'),
            icon: "warning"
          });
          return;
        }

        let acreditado: Acreditado = new Object() as Acreditado;
        acreditado.tipoAcreditado = { "id": 2473 } as TipoAcreditado;
        acreditado.estadoAcreditado = { "id": VERIFICADO } as EstadoAcreditado;
        acreditado.origenAcreditado = { "id": ORIGEN_ACREDITADO } as OrigenAcreditado;
        acreditado.modificado = SAVE;
        acreditado.cantidad = 1;
        acreditado.notificar = true;
        acreditado.notificarWhatsApp = false;

        acreditado.dato8 = this.formData.documento;
        acreditado.nombre = this.formData.nombre;
        acreditado.apellido = this.formData.apellido;
        acreditado.mail = this.formData.mail;
        acreditado.telefono = (this.selectedPhoneCountry ? this.selectedPhoneCountry.code : '') + this.formData.codigoArea + this.formData.telefonoCelular;
        acreditado.empresa = this.formData.empresa;

        acreditado.dato2 = this.selectedCountry ? this.selectedCountry.value : '';
        acreditado.dato9 = this.formData.industria;

        await suiteService.saveOrUpdate(suiteConfig.evento, acreditado);

        Swal.fire({
          title: this.$t('alert.exito.titulo'),
          html: this.$t('alert.exito.msg'),
          icon: "success",
        }).then(() => {
          this.$router.push({ path: '/finalizado' });
        });

      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
      return;
    }
  },
  async mounted() {
    // Acreditación libre, no requiere carga de hash ni pre-completado de campos
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>